import AccessTimeIcon from '@mui/icons-material/AccessTime';
import BadgeOutlinedIcon from '@mui/icons-material/BadgeOutlined';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import PersonOutlineIcon from '@mui/icons-material/PersonOutlined';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';
import Box from '@mui/material/Box';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import { alpha, useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { CloseIcon, WhatsAppIcon } from 'assets/icons';
import { PrimaryButton } from 'components/forms/atoms/FormButtons';
import { useFeedback } from 'contexts/FeedbackContext';
import moment from 'moment';
import { useDeleteAppointmentMutation, useUpdateAppointmentMutation } from 'services/api/appointments.api';
import {
  APPOINTMENT_STATUS_LABELS,
  APPOINTMENT_TYPE_LABELS,
  type Appointment,
  appointmentClientName,
} from 'services/types/appointment';
import { NAVY, RADIUS_CARD, RADIUS_ITEM, SURFACE_CONTAINER_LOWEST, SURFACE_TINT, WHATSAPP_GREEN } from 'styles/tokens';

import { STATUS_STYLES, TYPE_STYLES } from './serviceCalendar.constants';
import { capitalize, formatLongDate, formatTimeRange } from './serviceCalendar.utils';

const LABEL_COLOR = 'rgba(21, 24, 58, 0.45)';
const VALUE_COLOR = 'rgba(21, 24, 58, 0.85)';

const SectionLabel = ({ children }: { children: React.ReactNode }) => (
  <Typography
    sx={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.06em', color: LABEL_COLOR, textTransform: 'uppercase' }}
  >
    {children}
  </Typography>
);

const InfoRow = ({ icon, children }: { icon: React.ReactNode; children: React.ReactNode }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
    <Box sx={{ color: 'rgba(21, 24, 58, 0.4)', display: 'flex' }}>{icon}</Box>
    <Typography sx={{ fontSize: 13.5, color: VALUE_COLOR }}>{children}</Typography>
  </Box>
);

const CarField = ({ label, value }: { label: string; value: React.ReactNode }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
    <Typography
      sx={{ fontSize: 10.5, fontWeight: 600, letterSpacing: '0.05em', color: LABEL_COLOR, textTransform: 'uppercase' }}
    >
      {label}
    </Typography>
    <Typography sx={{ fontSize: 13.5, fontWeight: 600, color: NAVY }}>{value || '—'}</Typography>
  </Box>
);

interface Props {
  appointment: Appointment | null;
  open: boolean;
  onClose: () => void;
}

const AppointmentDetail = ({ appointment, open, onClose }: Props) => {
  const theme = useTheme();
  const { notifySaved } = useFeedback();
  const [updateAppointment, { isLoading: isUpdating }] = useUpdateAppointmentMutation();
  const [deleteAppointment, { isLoading: isDeleting }] = useDeleteAppointmentMutation();

  const typeStyle = appointment ? TYPE_STYLES[appointment.type] : null;
  const statusStyle = appointment ? STATUS_STYLES[appointment.status] : null;

  const car = appointment?.car;
  const carModel = car ? [car.brand, car.model].filter(Boolean).join(' ') : null;

  const markAsDone = async () => {
    if (!appointment) return;
    await updateAppointment({ id: appointment.id, changes: { status: 'done' } }).unwrap();
    notifySaved('Cita actualizada');
    onClose();
  };

  const remove = async () => {
    if (!appointment) return;
    await deleteAppointment(appointment.id).unwrap();
    notifySaved('Cita eliminada');
    onClose();
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      slotProps={{
        paper: {
          sx: {
            width: { xs: '100%', sm: 440 },
            backgroundColor: SURFACE_CONTAINER_LOWEST,
            border: 0,
            boxShadow: '0px 12px 40px rgba(21, 24, 58, 0.16)',
            display: 'flex',
            flexDirection: 'column',
          },
        },
      }}
    >
      {appointment && typeStyle && statusStyle && (
        <>
          {/* Header — tinted with the appointment type color */}
          <Box sx={{ backgroundColor: typeStyle.surface, px: 3, pt: 2.5, pb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: typeStyle.rail }} />
                <Typography
                  sx={{
                    fontSize: 11.5,
                    fontWeight: 700,
                    letterSpacing: '0.06em',
                    color: typeStyle.accent,
                    textTransform: 'uppercase',
                  }}
                >
                  {APPOINTMENT_TYPE_LABELS[appointment.type]}
                </Typography>
                <Typography sx={{ fontSize: 12.5, color: 'rgba(21, 24, 58, 0.5)' }}>
                  · {appointment.branchOffice}
                </Typography>
              </Box>
              <IconButton
                onClick={onClose}
                size="small"
                sx={{
                  backgroundColor: 'rgba(255,255,255,0.6)',
                  color: 'rgba(21, 24, 58, 0.6)',
                  '&:hover': { backgroundColor: '#fff', color: NAVY },
                }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
            <Typography sx={{ fontSize: 24, fontWeight: 700, color: NAVY, mt: 1.5 }}>
              {appointmentClientName(appointment)}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.75 }}>
              <AccessTimeIcon sx={{ fontSize: 15, color: 'rgba(21, 24, 58, 0.5)' }} />
              <Typography sx={{ fontSize: 13, color: 'rgba(21, 24, 58, 0.6)' }}>
                {capitalize(formatLongDate(moment(appointment.date)))} · {formatTimeRange(appointment)}
              </Typography>
            </Box>
          </Box>

          {/* Body */}
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              overflow: 'auto',
              px: 3,
              py: 3,
              display: 'flex',
              flexDirection: 'column',
              gap: 3,
            }}
          >
            <Box
              sx={{
                alignSelf: 'flex-start',
                px: 1,
                py: 0.35,
                borderRadius: RADIUS_ITEM,
                backgroundColor: statusStyle.surface,
              }}
            >
              <Typography
                sx={{
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: '0.05em',
                  textTransform: 'uppercase',
                  color: statusStyle.color,
                }}
              >
                {APPOINTMENT_STATUS_LABELS[appointment.status]}
              </Typography>
            </Box>

            {/* Cliente */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
              <SectionLabel>Cliente</SectionLabel>
              <InfoRow icon={<PersonOutlineIcon sx={{ fontSize: 18 }} />}>{appointmentClientName(appointment)}</InfoRow>
              <InfoRow icon={<BadgeOutlinedIcon sx={{ fontSize: 18 }} />}>{appointment.client?.rut || '—'}</InfoRow>
              <InfoRow icon={<PhoneOutlinedIcon sx={{ fontSize: 18 }} />}>{appointment.client?.phone || '—'}</InfoRow>
              <InfoRow icon={<EmailOutlinedIcon sx={{ fontSize: 18 }} />}>{appointment.client?.email || '—'}</InfoRow>
            </Box>

            {/* Vehículo */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
              <SectionLabel>Vehículo</SectionLabel>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: 2,
                  p: 2,
                  borderRadius: RADIUS_CARD,
                  backgroundColor: SURFACE_TINT,
                }}
              >
                <CarField label="Modelo" value={carModel} />
                <CarField label="Patente" value={car?.licensePlate} />
                <CarField label="Año" value={car?.year} />
                <CarField
                  label="KM de ingreso"
                  value={car?.km != null ? `${car.km.toLocaleString('es-CL')} km` : null}
                />
                <CarField label="Origen" value={appointment.client?.source} />
                {appointment.maintenanceInterval && (
                  <CarField label="Mantención" value={appointment.maintenanceInterval} />
                )}
              </Box>
            </Box>

            {/* Ejecutivo */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <SectionLabel>Ejecutivo asignado</SectionLabel>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Box
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: '50%',
                    backgroundColor: 'rgba(21, 24, 58, 0.06)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: NAVY,
                    fontWeight: 700,
                    fontSize: 13,
                  }}
                >
                  {(appointment.advisorName || '—').trim().charAt(0).toUpperCase()}
                </Box>
                <Box>
                  <Typography sx={{ fontSize: 14, fontWeight: 600, color: NAVY }}>
                    {appointment.advisorName || 'Sin asignar'}
                  </Typography>
                  <Typography sx={{ fontSize: 12, color: 'rgba(21, 24, 58, 0.55)' }}>
                    Asesor de servicio · {appointment.branchOffice}
                  </Typography>
                </Box>
              </Box>
            </Box>

            {/* Notas */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <SectionLabel>Notas</SectionLabel>
              <Box sx={{ p: 2, borderRadius: RADIUS_ITEM, backgroundColor: SURFACE_TINT }}>
                <Typography sx={{ fontSize: 13.5, color: appointment.notes ? VALUE_COLOR : 'rgba(21, 24, 58, 0.4)' }}>
                  {appointment.notes || 'Sin notas.'}
                </Typography>
              </Box>
            </Box>

            {/* WhatsApp */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <SectionLabel>WhatsApp</SectionLabel>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 1,
                  p: 1.5,
                  borderRadius: RADIUS_ITEM,
                  backgroundColor: 'rgba(37, 211, 102, 0.08)',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <WhatsAppIcon sx={{ fontSize: 20, color: WHATSAPP_GREEN }} />
                  <Typography sx={{ fontSize: 13, color: VALUE_COLOR }}>
                    {appointment.client?.phone || 'Sin teléfono'}
                  </Typography>
                </Box>
                {appointment.clientId && (
                  <Box
                    component="a"
                    href={`/clients/${appointment.clientId}`}
                    target="_blank"
                    rel="noreferrer"
                    sx={{
                      fontSize: 12.5,
                      fontWeight: 600,
                      color: WHATSAPP_GREEN,
                      textDecoration: 'none',
                      px: 1.25,
                      py: 0.5,
                      borderRadius: RADIUS_ITEM,
                      '&:hover': { backgroundColor: 'rgba(37, 211, 102, 0.14)' },
                    }}
                  >
                    Ver conversación
                  </Box>
                )}
              </Box>
            </Box>
          </Box>

          {/* Footer */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1.5,
              px: 3,
              py: 2,
              boxShadow: 'inset 0 1px 0 rgba(21, 24, 58, 0.06)',
            }}
          >
            <Box
              component="button"
              onClick={remove}
              disabled={isDeleting}
              sx={{
                border: 0,
                background: 'none',
                cursor: 'pointer',
                fontSize: 13,
                fontWeight: 600,
                fontFamily: 'inherit',
                color: theme.palette.error.main,
                px: 1,
                py: 0.5,
                borderRadius: RADIUS_ITEM,
                '&:hover': { backgroundColor: alpha(theme.palette.error.main, 0.08) },
                '&:disabled': { opacity: 0.5, cursor: 'default' },
              }}
            >
              Eliminar
            </Box>
            {appointment.status === 'pending' && (
              <PrimaryButton onClick={markAsDone} disabled={isUpdating}>
                {isUpdating ? 'Guardando…' : 'Marcar como realizada'}
              </PrimaryButton>
            )}
          </Box>
        </>
      )}
    </Drawer>
  );
};

export default AppointmentDetail;
